import { OrderSide } from 'communication/types/order/OrderSide';
import {
    PortfolioPosition,
    PortfolioSymbolQuantity,
} from 'portfolio/PortfolioSelection';
import Order from 'communication/types/order/Order';
import {
    usePortfolioSelectedSymbolQuantity,
    usePortfolioStreamedOrSelectedPositionFromOrders,
    useSelectPortfolioStreamedOrSelectedOrders,
} from 'portfolio/store/hooks';

export interface FillsGraphPortfolioData {
    side: OrderSide;
    totalOrderQty: number;
    totalFillQty: number;
    avgFillPrice: number;
    percentageFilled: number;
    hasFullOrderData: boolean;
    orderLimitPrice?: number;
}

export function useFillsGraphPortfolioData():
    | FillsGraphPortfolioData
    | undefined {
    const streamingOrdersPosition =
        usePortfolioStreamedOrSelectedPositionFromOrders();
    const streamingOrders = useSelectPortfolioStreamedOrSelectedOrders();
    const selectionSymQty = usePortfolioSelectedSymbolQuantity();
    return getFillsGraphPortfolioData(
        streamingOrdersPosition,
        streamingOrders,
        selectionSymQty
    );
}

export function getFillsGraphPortfolioData(
    streamingOrdersPosition: PortfolioPosition | undefined,
    portfolioStreamingOrders: Order[] | undefined,
    symQty: PortfolioSymbolQuantity | undefined
): FillsGraphPortfolioData | undefined {
    if (portfolioStreamingOrders && !streamingOrdersPosition) {
        // When we are using the orders stream, we must have a single position...
        return undefined;
    }
    if (!portfolioStreamingOrders && !symQty) {
        // When this is being sourced from the selection rather than the orders stream
        // (eq symbol override or Newport "port"), we need a SymbolQuantity
        return undefined;
    }

    const position = streamingOrdersPosition ?? symQty!.netPosition;

    const ret: FillsGraphPortfolioData = {
        side: position.side,
        totalOrderQty: 0,
        totalFillQty: 0,
        avgFillPrice: 0,
        percentageFilled: 0,
        hasFullOrderData: false,
    };

    if (!portfolioStreamingOrders) {
        ret.side = position.side;
        ret.totalOrderQty = position.qty;
        return ret;
    }

    ret.hasFullOrderData = true;

    if (portfolioStreamingOrders.length === 1) {
        const orderLimitPrice = portfolioStreamingOrders[0].limit;
        ret.orderLimitPrice =
            orderLimitPrice && orderLimitPrice > 0
                ? orderLimitPrice
                : undefined;
    }

    portfolioStreamingOrders.reduce((value, order) => {
        value.totalOrderQty = value.totalOrderQty + order.qty;
        value.totalFillQty = value.totalFillQty + order.fillQty ?? 0;
        value.avgFillPrice =
            value.avgFillPrice + (order.avgPx ?? 0) * order.fillQty;
        return value;
    }, ret);

    ret.avgFillPrice = ret.totalFillQty
        ? ret.avgFillPrice / ret.totalFillQty
        : 0;
    ret.percentageFilled = ret.totalOrderQty
        ? (ret.totalFillQty / ret.totalOrderQty) * 100
        : 0;

    return ret;
}
